import { useEffect, useState } from "react";
import { Menu, X, MessageCircle, Languages } from "lucide-react";
import { Logo } from "./Logo";
import { useLang } from "@/i18n";

const WHATSAPP_URL =
  "https://wa.me/9647760506057?text=%D9%85%D8%B1%D8%AD%D8%A8%D9%8B%D8%A7%20%D9%85%D8%B1%D9%83%D8%B2%20%D9%83%D9%88%D9%86%D9%83%D8%AA%D8%AF%D8%8C%20%D8%A3%D8%B1%D8%BA%D8%A8%20%D8%A8%D8%A7%D9%84%D8%A7%D8%B3%D8%AA%D9%81%D8%B3%D8%A7%D8%B1%20%D8%B9%D9%86%20%D8%AE%D8%AF%D9%85%D8%A7%D8%AA%D9%83%D9%85";

const NAV_ITEMS = [
  { id: "nav-link-hero", href: "#hero" },
  { id: "nav-link-services", href: "#services" },
  { id: "nav-link-colors", href: "#colors" },
  { id: "nav-link-brochures", href: "#brochures" },
  { id: "nav-link-works", href: "#works" },
  { id: "nav-link-warranty", href: "#warranty" },
  { id: "nav-link-contact", href: "#contact" },
];

export const LangToggle = ({ className = "" }) => {
  const { lang, toggle } = useLang();
  return (
    <button
      data-testid="language-toggle-button"
      onClick={toggle}
      aria-label={lang === "ar" ? "Switch to English" : "التبديل إلى العربية"}
      className={`inline-flex items-center gap-1.5 rounded-full border border-[#F5F2EB]/20 px-4 py-2 text-xs font-bold tracking-wider text-[#F5F2EB] transition-all duration-300 hover:border-[#E3AF4F]/60 hover:bg-[#F5F2EB]/5 ${className}`}
    >
      <Languages size={14} className="text-[#E3AF4F]" />
      {lang === "ar" ? "EN" : "عربي"}
    </button>
  );
};

export default function Navbar() {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 border-b transition-all duration-300 ${
        scrolled
          ? "border-[#F5F2EB]/10 bg-[#000000]/85 backdrop-blur-xl"
          : "border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 sm:px-8">
        <a href="#hero" aria-label={t.nav.homeAria}>
          <Logo />
        </a>
        <nav className="hidden items-center gap-7 lg:flex" aria-label="Main navigation">
          {NAV_ITEMS.map((item, i) => (
            <a
              key={item.id}
              data-testid={item.id}
              href={item.href}
              className="text-sm font-medium text-[#A8A29E] transition-colors duration-300 hover:text-[#F5F2EB]"
            >
              {t.nav.links[i]}
            </a>
          ))}
        </nav>
        <div className="hidden items-center gap-3 lg:flex">
          <LangToggle />
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-cta-whatsapp-button"
            className="inline-flex items-center gap-2 rounded-full bg-[#E3AF4F] px-6 py-2.5 text-sm font-bold text-[#000000] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#F5D08C]"
          >
            <MessageCircle size={16} />
            {t.nav.cta}
          </a>
        </div>
        <div className="flex items-center gap-3 lg:hidden">
          <LangToggle />
          <button
            data-testid="nav-mobile-menu-button"
            className="text-[#F5F2EB]"
            onClick={() => setOpen((v) => !v)}
            aria-label={t.nav.menuLabel}
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="flex flex-col gap-4 border-t border-[#F5F2EB]/10 bg-[#000000]/95 px-5 py-6 backdrop-blur-xl lg:hidden">
          {NAV_ITEMS.map((item, i) => (
            <a
              key={item.id}
              data-testid={`${item.id}-mobile`}
              href={item.href}
              onClick={() => setOpen(false)}
              className="text-base font-medium text-[#F5F2EB]"
            >
              {t.nav.links[i]}
            </a>
          ))}
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-cta-whatsapp-button-mobile"
            className="mt-2 rounded-full bg-[#E3AF4F] px-6 py-3 text-center text-sm font-bold text-[#000000]"
          >
            {t.nav.cta}
          </a>
        </div>
      )}
    </header>
  );
}
